// OPS-2 (2026-05-31) — claim-level Attention Panel. Surfaces the active
// attention_items for one claim near the top of ClaimDetail, with inline
// Acknowledge / Resolve. Restrained visual language (Info/Warning/Critical) —
// attention, not panic. Renders nothing when the claim is clear.
import { useState, useEffect, useCallback } from "react";
import { apiFetch, getPermissions } from "../api";

const SEV = {
  Critical: { bg: "#fef2f2", border: "#dc2626", dot: "#dc2626", label: "#991b1b" },
  Warning:  { bg: "#fffbeb", border: "#d97706", dot: "#d97706", label: "#92400e" },
  Info:     { bg: "#f8fafc", border: "#94a3b8", dot: "#64748b", label: "#475569" },
};
const ICON = {
  OVERDUE_TASK: "⏰", HIGH_PRIORITY_TASK: "⚑", RESERVE_REVIEW: "💰",
  CLAIM_INACTIVE: "💤", DOCUMENT_REVIEW: "📄", PAYMENT_REVIEW: "💵",
  PAYMENT_CODING_REQUIRED: "🧾", MISSING_CLAIMANT_INFO: "🧍",
};
// CLAIMANT-INJURY-DASHBOARD-1 (P6): which types deep-link (have a destination tab).
const NAVIGABLE = new Set(["OVERDUE_TASK", "HIGH_PRIORITY_TASK", "RESERVE_REVIEW",
  "DOCUMENT_REVIEW", "PAYMENT_REVIEW", "PAYMENT_CODING_REQUIRED", "MISSING_CLAIMANT_INFO"]);

export default function ClaimAttentionPanel({ claimId, onWorkChanged, refreshKey, onGo, bare }) {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    apiFetch(`/claims/${claimId}/attention`).then(d => setItems(d || [])).catch(() => setItems([]));
  }, [claimId]);
  // refreshKey (OPS-3): re-fetch when a bill transition bumps app workRefresh,
  // so the panel clears the moment a PAYMENT_REVIEW item resolves.
  useEffect(() => { load(); }, [load, refreshKey]);

  // WS0-I (2026-09-10): PATCH /attention/{id} moved off the claim READ cap onto
  // the claim EDIT pair (Master SOP §8), so `accountant` and `ownership` — the two
  // roles the matrix gives no claim-edit authority at all — now get a 403 here.
  // Hide the controls from them rather than leaving a button that only fails.
  // Deliberately keyed on holding the capability with ANY scope, not on
  // canOnClaim(): this panel is handed a claimId, not a claim object, and an
  // assigned-scope check without one would hide the buttons from the adjuster who
  // CAN act. The server is the gate either way — this is UI hinting only.
  const mayEdit = (() => {
    const perms = getPermissions();
    const cc = perms.claim_capabilities || {};
    // LD-REV-042: the six workforce roles hold ack_attention (accountant/ownership included).
    return !!(cc.edit_claim || cc.edit_claim_basic || (perms.capabilities || []).includes("ack_attention"));
  })();

  const act = async (id, action) => {
    setBusy(id);
    try { await apiFetch(`/attention/${id}`, { method: "PATCH", body: { action } }); load(); if (onWorkChanged) onWorkChanged(); }
    catch (e) { alert(e.message || "Action failed."); }
    setBusy(null);
  };

  if (!items.length) return null;

  // `bare` (CLAIM-OVERVIEW-UX-2): drop the internal header + outer margin so this
  // can sit inside the Overview command-rail card (which supplies its own header).
  return (
    <div style={{ margin: bare ? 0 : "0 0 14px" }}>
      {!bare && (
        <div style={{ fontSize: 12, fontWeight: 700, color: "var(--text2)", textTransform: "uppercase", letterSpacing: 0.4, marginBottom: 6 }}>
          Attention <span style={{ color: "var(--text3)", fontWeight: 400 }}>({items.length})</span>
        </div>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {items.map(it => {
          const s = SEV[it.severity] || SEV.Info;
          return (
            <div key={it.id} style={{ background: s.bg, border: `1px solid ${s.border}33`, borderLeft: `3px solid ${s.border}`, borderRadius: 6, padding: "8px 12px" }}>
              {(() => { const go = onGo && NAVIGABLE.has(it.type); return (
              <div onClick={go ? () => onGo(it) : undefined} title={go ? "Open the related section" : undefined}
                   style={{ minWidth: 0, cursor: go ? "pointer" : "default" }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: s.label, display: "flex", gap: 6 }}>
                  <span style={{ fontSize: 14, flexShrink: 0 }}>{ICON[it.type] || "•"}</span>
                  <span style={{ minWidth: 0 }}>{it.title}
                    {go && <span style={{ marginLeft: 6, fontSize: 11, color: s.border }}>→</span>}
                    {it.status === "Acknowledged" && <span style={{ marginLeft: 8, fontSize: 10, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase" }}>· acknowledged</span>}
                  </span>
                </div>
                {it.description && <div style={{ fontSize: 11.5, color: "var(--text2)", marginTop: 2, paddingLeft: 20 }}>{it.description}</div>}
              </div>
              ); })()}
              {mayEdit && (
              <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                {it.status === "Open" && (
                  <button className="btn btn-outline btn-sm" disabled={busy === it.id} onClick={() => act(it.id, "acknowledge")} title="Mark as seen — stays until the condition clears">Acknowledge</button>
                )}
                <button className="btn btn-outline btn-sm" disabled={busy === it.id} onClick={() => act(it.id, "resolve")} title="Dismiss now (re-appears next sweep if still true)">Resolve</button>
              </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
